import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaSpinner, FaEnvelope, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { UniversityAuthService } from '../../services/UniversityAuthService';
import { useSnackBar } from '../../hooks/useSnackBar';

const UniversityEmailVerification = () => {
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const enqMsg = useSnackBar();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyEmail(token);
    } else {
      setVerificationStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await UniversityAuthService.verifyEmail(token);
      
      if (response.success) {
        setVerificationStatus('success');
        setMessage('Email verified successfully! You can now log in to your account.');
        setEmail(response.user?.email || '');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/university/login');
        }, 3000);
      } else {
        setVerificationStatus('error');
        setMessage(response.message || 'Email verification failed. Please try again.');
      }
    } catch (error) {
      setVerificationStatus('error');
      setMessage(error.message || 'An error occurred during verification. Please try again.');
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      enqMsg('Please enter your email address', 'error');
      return;
    }

    setIsResending(true);
    try {
      const response = await UniversityAuthService.resendVerification(email);
      
      if (response.success) {
        enqMsg('Verification email sent! Please check your inbox.', 'success');
      } else {
        enqMsg(response.message || 'Failed to resend verification email', 'error');
      }
    } catch (error) {
      enqMsg(error.message || 'Failed to resend verification email', 'error');
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
              <FaSpinner className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verifying Email</h2>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <FaCheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Email Verified!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                You will be redirected to the login page in a few seconds...
              </p>
            </div>
            <Link
              to="/university/login"
              className="inline-flex items-center px-6 py-3 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-colors duration-200"
            >
              Go to Login
            </Link>
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <FaExclamationTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your email to resend verification
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@university.edu"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent"
                    />
                    <button
                      onClick={handleResendVerification}
                      disabled={isResending || !email}
                      className="px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isResending ? (
                        <FaSpinner className="h-4 w-4 animate-spin" />
                      ) : (
                        'Resend'
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500">
                  <p>• Check your spam/junk folder</p>
                  <p>• Make sure you're using the correct email address</p>
                  <p>• Contact support if the problem persists</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 space-x-4">
              <Link
                to="/university/login"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <FaArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Link>
              <Link
                to="/university/signup"
                className="inline-flex items-center px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-colors duration-200"
              >
                Try Signing Up Again
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFE5E3] via-white to-[#F8F9FA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-full flex items-center justify-center shadow-lg shadow-[#DB4F40]/25">
            <FaEnvelope className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          {renderContent()}
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-[#DB4F40] transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UniversityEmailVerification;
